import React, { useState, useEffect, useContext } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AuthContext } from '../App';
import { formatCurrency } from '../utils/formatUtils';
import { 
  ArrowLeft, UserPlus, UserCheck, Users, TrendingUp, 
  DollarSign, BarChart3, Filter, ChevronDown, X, Plus, Trash2,
  Eye, Calendar, HelpCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sales');
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [retentionData, setRetentionData] = useState(null);
  
  // Filters — по умолчанию текущий месяц
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(true);

  // Add custom manager
  const [showAddManager, setShowAddManager] = useState(false);
  const [addManagerSearch, setAddManagerSearch] = useState('');

  // Manager detail modal
  const [detailManager, setDetailManager] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailDateFrom, setDetailDateFrom] = useState('');
  const [detailDateTo, setDetailDateTo] = useState('');

  const months = [
    { value: '', label: tt('Все месяцы') },
    { value: 1, label: tt('Январь') }, { value: 2, label: tt('Февраль') },
    { value: 3, label: tt('Март') }, { value: 4, label: tt('Апрель') },
    { value: 5, label: tt('Май') }, { value: 6, label: tt('Июнь') },
    { value: 7, label: tt('Июль') }, { value: 8, label: tt('Август') },
    { value: 9, label: tt('Сентябрь') }, { value: 10, label: tt('Октябрь') },
    { value: 11, label: tt('Ноябрь') }, { value: 12, label: tt('Декабрь') },
  ];

  const roleLabels = {
    superadmin: tt('Суперадмин'),
    admin: tt('Админ'),
    sales_manager: tt('Сейл'),
    team_lead_sales: tt('Тимлид сейл'),
    retention_manager: tt('Ретеншн'),
    team_lead_retention: tt('Тимлид ретеншн'),
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [activeTab, month, dateFrom, dateTo, selectedManagers]);

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(res.data || []);
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (month) {
        params.append('month', month);
        params.append('year', now.getFullYear());
      }
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      selectedManagers.forEach(id => params.append('manager_id', id));

      const endpoint = activeTab === 'sales' ? 'stats/sales' : 'stats/retention';
      const res = await axios.get(`${API}/${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (activeTab === 'sales') setSalesData(res.data);
      else setRetentionData(res.data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomManager = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stats/custom-managers`, {
        user_id: userId,
        department: activeTab
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowAddManager(false);
      setAddManagerSearch('');
      fetchStats();
    } catch (e) {
      alert(e.response?.data?.detail || 'Ошибка при добавлении');
    }
  };

  const handleRemoveCustomManager = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/stats/custom-managers/${activeTab}/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStats();
    } catch (e) {
      alert(e.response?.data?.detail || 'Ошибка при удалении');
    }
  };

  const openManagerDetail = (manager) => {
    setDetailManager(manager);
    setDetailData(null);
    setDetailDateFrom('');
    setDetailDateTo('');
    fetchManagerDetail(manager.id);
  };

  const fetchManagerDetail = async (managerId, dFrom, dTo) => {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (dFrom || dTo) {
        if (dFrom) params.append('date_from', dFrom);
        if (dTo) params.append('date_to', dTo);
      } else if (month) {
        params.append('month', month);
        params.append('year', now.getFullYear());
      }

      const dept = activeTab === 'sales' ? 'sales' : 'retention';
      const res = await axios.get(`${API}/stats/${dept}/${managerId}/details?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailData(res.data);
    } catch (e) {
      console.error('Error fetching manager detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const applyDetailFilters = () => {
    if (detailManager) fetchManagerDetail(detailManager.id, detailDateFrom, detailDateTo);
  };

  const clearDetailFilters = () => {
    setDetailDateFrom('');
    setDetailDateTo('');
    if (detailManager) fetchManagerDetail(detailManager.id, '', '');
  };

  const clearFilters = () => {
    setMonth(now.getMonth() + 1);
    setDateFrom('');
    setDateTo('');
    setSelectedManagers([]);
  };

  const hasActiveFilters = dateFrom || dateTo || selectedManagers.length > 0 || month !== (now.getMonth() + 1);

  const data = activeTab === 'sales' ? salesData : retentionData;

  // Filter users for add-manager modal — only staff, not clients
  const currentManagerIds = data ? data.managers.map(m => m.id) : [];
  const addableUsers = allUsers.filter(u => 
    u.role !== 'client' &&
    !currentManagerIds.includes(u.id) &&
    u.name.toLowerCase().includes(addManagerSearch.toLowerCase())
  );

  // Managers for filter dropdown (from current data)
  const filterableManagers = data ? data.managers : [];

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/crm/dashboard')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="stats-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="page-title" data-testid="stats-page-title">{tt('Статистика')}</h1>
            <p className="page-subtitle">{tt('Детальная аналитика по отделам')}</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddManager(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          data-testid="add-manager-btn"
        >
          <Plus className="w-4 h-4" />
          {tt('Добавить сотрудника')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit" data-testid="stats-tabs">
        <button
          onClick={() => { setActiveTab('sales'); setSelectedManagers([]); }}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'sales' 
              ? 'bg-white text-blue-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-sales"
        >
          <UserPlus className="w-4 h-4 inline mr-2" />
          {tt('Сейл отдел')}
        </button>
        <button
          onClick={() => { setActiveTab('retention'); setSelectedManagers([]); }}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'retention' 
              ? 'bg-white text-green-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-retention"
        >
          <UserCheck className="w-4 h-4 inline mr-2" />
          {tt('Ретеншн отдел')}
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700"
          >
            <Filter className="w-4 h-4" />
            {tt('Фильтры')}
            {hasActiveFilters && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">{tt('Активны')}</span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? '' : 'rotate-180'}`} />
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
              <X className="w-3 h-3" /> {tt('Сбросить')}
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{tt('Месяц')}</label>
              <select 
                value={month} 
                onChange={e => setMonth(e.target.value ? parseInt(e.target.value) : '')}
                className="form-select text-sm w-full"
                data-testid="filter-month"
              >
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{tt('Дата от')}</label>
              <input 
                type="date" value={dateFrom} 
                onChange={e => { setDateFrom(e.target.value); if (e.target.value) setMonth(''); }}
                className="form-input text-sm w-full"
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{tt('Дата до')}</label>
              <input 
                type="date" value={dateTo} 
                onChange={e => { setDateTo(e.target.value); if (e.target.value) setMonth(''); }}
                className="form-input text-sm w-full"
                data-testid="filter-date-to"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{tt('Сотрудник')}</label>
              <select 
                value={selectedManagers[0] || ''}
                onChange={e => setSelectedManagers(e.target.value ? [parseInt(e.target.value)] : [])}
                className="form-select text-sm w-full"
                data-testid="filter-manager"
              >
                <option value="">{tt('Все')}</option>
                {filterableManagers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-16">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : !data ? (
        <div className="text-center text-gray-500 p-16">{tt('Нет данных')}</div>
      ) : (
        <>
          {activeTab === 'sales' ? (
            <SalesDepartmentView data={data} onRemoveCustom={handleRemoveCustomManager} roleLabels={roleLabels} onManagerClick={openManagerDetail} />
          ) : (
            <RetentionDepartmentView data={data} onRemoveCustom={handleRemoveCustomManager} roleLabels={roleLabels} onManagerClick={openManagerDetail} />
          )}
        </>
      )}

      {/* Add Manager Modal */}
      {showAddManager && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddManager(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                Добавить сотрудника в {activeTab === 'sales' ? 'Сейл' : 'Ретеншн'}
              </h3>
              <button onClick={() => setShowAddManager(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b">
              <input
                type="text"
                value={addManagerSearch}
                onChange={e => setAddManagerSearch(e.target.value)}
                placeholder={tt('Поиск по имени...')}
                className="form-input text-sm w-full"
                autoFocus
                data-testid="add-manager-search"
              />
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {addableUsers.length === 0 ? (
                <p className="text-center text-gray-400 py-4 text-sm">{tt('Нет доступных сотрудников')}</p>
              ) : (
                addableUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => handleAddCustomManager(u.id)}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 rounded-lg transition-colors text-left"
                    data-testid={`add-manager-${u.id}`}
                  >
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{u.name}</div>
                      <div className="text-xs text-gray-400">{roleLabels[u.role] || u.role}</div>
                    </div>
                    <Plus className="w-4 h-4 text-blue-500" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manager Detail Modal */}
      {detailManager && (
        <ManagerDetailModal
          manager={detailManager}
          data={detailData}
          loading={detailLoading}
          department={activeTab}
          roleLabels={roleLabels}
          dateFrom={detailDateFrom}
          dateTo={detailDateTo}
          onDateFromChange={setDetailDateFrom}
          onDateToChange={setDetailDateTo}
          onApplyFilters={applyDetailFilters}
          onClearFilters={clearDetailFilters}
          onClose={() => setDetailManager(null)}
        />
      )}
    </div>
  );
};

// ==================== SALES VIEW ====================
const SalesDepartmentView = ({ data, onRemoveCustom, roleLabels, onManagerClick }) => {
  const { tt } = useLanguage();
  const dept = data.department;
  
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="sales-dept-summary">
        <StatCard icon={<Users className="w-5 h-5 text-blue-600" />} label={tt('Новых лидов')} value={dept.newLeads} bg="bg-blue-50" color="text-blue-700" formula={tt('Новые лиды = клиенты, зарегистрированные в выбранном периоде (по дате регистрации)')} />
        <StatCard icon={<TrendingUp className="w-5 h-5 text-green-600" />} label={tt('FTD клиенты')} value={dept.ftdClients} bg="bg-green-50" color="text-green-700" sub={dept.ftdClean > 0 || dept.ftdDirty > 0 ? `чист: ${dept.ftdClean} / гряз: ${dept.ftdDirty}` : null} formula={tt('FTD = уникальные клиенты, у которых первый депозит (тип ftd) был создан в выбранном периоде. Чистый = баланс ≥ 95% от FTD-суммы, грязный = меньше.')} />
        <StatCard icon={<BarChart3 className="w-5 h-5 text-amber-600" />} label={tt('Конверсия')} value={`${dept.conversion}%`} bg="bg-amber-50" color="text-amber-700" formula={tt('Конверсия = FTD / Новые лиды × 100%')} />
        <StatCard icon={<DollarSign className="w-5 h-5 text-purple-600" />} label={tt('Сумма депозитов')} value={formatCurrency(dept.depositAmount, true)} bg="bg-purple-50" color="text-purple-700" formula={tt('Сумма всех FTD-депозитов сейл-менеджеров за период')} />
      </div>

      <div className="card overflow-hidden" data-testid="sales-managers-table">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900">{tt('По сотрудникам')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-left">
                <th className="px-4 py-3 font-medium">{tt('Сотрудник')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Новых лидов')}</th>
                <th className="px-4 py-3 font-medium text-right">FTD</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Конверсия')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Депозитов')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Сумма')}</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {data.managers.map((m, i) => (
                <tr key={m.id} onClick={() => onManagerClick(m)} className={`border-t cursor-pointer ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/50 transition-colors`} data-testid={`sales-manager-row-${m.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{m.name}</span>
                      {m.isCustom && <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">{tt('Добавлен')}</span>}
                      <Eye className="w-3.5 h-3.5 text-gray-300" />
                    </div>
                    <div className="text-xs text-gray-400">{roleLabels[m.role] || m.role}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-600">{m.newLeads}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600">
                    {m.ftdClients}
                    {(m.ftdClean > 0 || m.ftdDirty > 0) && (
                      <div className="text-[10px] font-normal text-gray-400">{m.ftdClean}/{m.ftdDirty}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      m.conversion >= 5 ? 'bg-green-100 text-green-700' : m.conversion > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                    }`}>{m.conversion}%</span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{m.totalDeposits}</td>
                  <td className="px-4 py-3 text-right font-semibold text-purple-600">{formatCurrency(m.depositAmount, true)}</td>
                  <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                    {m.isCustom && (
                      <button onClick={() => onRemoveCustom(m.id)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title={tt('Удалить из статистики')}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {data.managers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">{tt('Нет данных')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ==================== RETENTION VIEW ====================
const RetentionDepartmentView = ({ data, onRemoveCustom, roleLabels, onManagerClick }) => {
  const { tt } = useLanguage();
  const dept = data.department;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="retention-dept-summary">
        <StatCard icon={<Users className="w-5 h-5 text-green-600" />} label={tt('К-во лидов')} value={dept.leads} bg="bg-green-50" color="text-green-700" formula={tt('К-во лидов = клиенты, у которых FTD произошёл в выбранном периоде (поступили на ретеншн от сейлов)')} />
        <StatCard icon={<TrendingUp className="w-5 h-5 text-blue-600" />} label={tt('Релоадов')} value={`${dept.reloadCount} (${dept.reloadPercent}%)`} bg="bg-blue-50" color="text-blue-700" formula={tt('Релоад = из лидов периода, у скольких этот ретеншн менеджер сделал первый депозит после FTD. % = релоад / лиды × 100%')} />
        <StatCard icon={<DollarSign className="w-5 h-5 text-purple-600" />} label={tt('Ср. чек')} value={formatCurrency(dept.avgCheck, true)} bg="bg-purple-50" color="text-purple-700" formula={tt('Ср. чек = Сумма за месяц / К-во лидов (считается на всех лидов, а не только на тех кто внёс депозит)')} />
        <StatCard icon={<DollarSign className="w-5 h-5 text-emerald-600" />} label={tt('Сумма за месяц')} value={formatCurrency(dept.monthAmount, true)} bg="bg-emerald-50" color="text-emerald-700" formula={tt('Сумма за месяц = все non-FTD депозиты от лидов этого периода (созданы в этом периоде, кто бы ни добавил)')} />
        <StatCard icon={<DollarSign className="w-5 h-5 text-amber-600" />} label={tt('Тотал')} value={formatCurrency(dept.totalAmount, true)} bg="bg-amber-50" color="text-amber-700" formula={tt('Тотал = все non-FTD депозиты ретеншна, созданные в выбранном периоде (от любого клиента, в т.ч. старых)')} />
        <StatCard icon={<BarChart3 className="w-5 h-5 text-rose-600" />} label={tt('Кол-во депозитов')} value={dept.totalDeposits} bg="bg-rose-50" color="text-rose-700" formula={tt('Общее количество депозитов (любого типа), созданных ретеншном в выбранном периоде')} />
      </div>

      <div className="card overflow-hidden" data-testid="retention-managers-table">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900">{tt('По сотрудникам')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-left">
                <th className="px-4 py-3 font-medium">{tt('Сотрудник')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('К-во лидов')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Релоадов')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('% релоадов')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Ср. чек')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Сумма за месяц')}</th>
                <th className="px-4 py-3 font-medium text-right">{tt('Тотал')}</th>
                <th className="px-4 py-3 w-32 text-center">{tt('Действия')}</th>
              </tr>
            </thead>
            <tbody>
              {data.managers.map((m, i) => (
                <tr key={m.id} className={`border-t ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-green-50/50 transition-colors`} data-testid={`retention-manager-row-${m.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{m.name}</span>
                      {m.isCustom && <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">{tt('Добавлен')}</span>}
                    </div>
                    <div className="text-xs text-gray-400">{roleLabels[m.role] || m.role}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600">{m.leads}</td>
                  <td className="px-4 py-3 text-right text-blue-600">{m.reloadCount}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      m.reloadPercent >= 30 ? 'bg-green-100 text-green-700' : m.reloadPercent > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                    }`}>{m.reloadPercent}%</span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-purple-600">{formatCurrency(m.avgCheck, true)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-600">{formatCurrency(m.monthAmount, true)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-700">{formatCurrency(m.totalAmount, true)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onManagerClick(m)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded transition-colors"
                        data-testid={`retention-view-btn-${m.id}`}
                        title={tt('Посмотреть детали')}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>{tt('Посмотреть')}</span>
                      </button>
                      {m.isCustom && (
                        <button onClick={(e) => { e.stopPropagation(); onRemoveCustom(m.id); }} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title={tt('Удалить из статистики')}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {data.managers.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">{tt('Нет данных')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ==================== MANAGER DETAIL MODAL ====================
const ManagerDetailModal = ({
  manager, data, loading, department, roleLabels,
  dateFrom, dateTo, onDateFromChange, onDateToChange,
  onApplyFilters, onClearFilters, onClose
}) => {
  const { tt } = useLanguage();
  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d; }
  };

  const isSales = department === 'sales';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-6 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-auto" onClick={e => e.stopPropagation()} data-testid="manager-detail-modal">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between bg-gradient-to-r from-gray-50 to-white rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900" data-testid="detail-manager-name">{manager.name}</h2>
            <p className="text-sm text-gray-500">{roleLabels[manager.role] || manager.role} — {isSales ? tt('Сейл отдел') : tt('Ретеншн отдел')}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="detail-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b bg-gray-50/50 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{tt('Дата от')}</label>
            <input type="date" value={dateFrom} onChange={e => onDateFromChange(e.target.value)}
              className="form-input text-sm" data-testid="detail-date-from" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{tt('Дата до')}</label>
            <input type="date" value={dateTo} onChange={e => onDateToChange(e.target.value)}
              className="form-input text-sm" data-testid="detail-date-to" />
          </div>
          <button onClick={onApplyFilters}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="detail-apply-filters">
            {tt('Применить')}
          </button>
          {(dateFrom || dateTo) && (
            <button onClick={onClearFilters}
              className="px-3 py-2 text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
              data-testid="detail-clear-filters">
              <X className="w-3 h-3" /> {tt('Сбросить')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-16">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : !data ? (
          <div className="text-center text-gray-500 p-16">{tt('Нет данных')}</div>
        ) : (
          <div className="p-5 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Summary */}
            {isSales ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="detail-summary">
                <MiniStat label={tt('Лидов')} value={data.summary.totalLeads} color="text-blue-600" formula={tt('Клиенты, которых вёл этот сейл-менеджер в выбранном периоде')} />
                <MiniStat label="FTD" value={data.summary.ftdClients} color="text-green-600" formula={tt('Уникальные клиенты, у которых первый депозит создан этим менеджером в периоде')} />
                <MiniStat label={tt('Конверсия')} value={`${data.summary.conversion}%`} color="text-amber-600" formula={tt('FTD / Лидов × 100%')} />
                <MiniStat label={tt('Депозитов')} value={data.summary.totalDeposits} color="text-gray-700" formula={tt('Общее количество депозитов созданных этим менеджером в периоде')} />
                <MiniStat label={tt('Сумма')} value={formatCurrency(data.summary.depositAmount, true)} color="text-purple-600" formula={tt('Сумма всех депозитов созданных этим менеджером в периоде')} />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="detail-summary">
                <MiniStat label={tt('К-во лидов')} value={data.summary.leads} color="text-green-600" formula={tt('Клиенты с FTD в периоде, где этот менеджер — первый ретеншн-комментатор')} />
                <MiniStat label={tt('Релоадов')} value={data.summary.reloadCount} color="text-blue-600" formula={tt('Из лидов: у скольких этот менеджер сделал первый депозит после FTD в периоде')} />
                <MiniStat label={tt('% релоадов')} value={`${data.summary.reloadPercent}%`} color="text-amber-600" formula={tt('Релоадов / К-во лидов × 100%')} />
                <MiniStat label={tt('Ср. чек')} value={formatCurrency(data.summary.avgCheck, true)} color="text-purple-600" formula={tt('Сумма за месяц / К-во лидов (все лиды, не только те кто внёс)')} />
                <MiniStat label={tt('Сумма за месяц')} value={formatCurrency(data.summary.monthAmount, true)} color="text-emerald-600" formula={tt('Non-FTD депозиты этого менеджера в периоде от лидов этого периода')} />
                <MiniStat label={tt('Тотал')} value={formatCurrency(data.summary.totalAmount, true)} color="text-gray-700" formula={tt('Все non-FTD депозиты этого менеджера в периоде (любой клиент, в т.ч. старые)')} />
              </div>
            )}

            {/* Campaign Breakdown */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-gray-400" />
                {tt('По кампаниям')}
              </h3>
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  {isSales ? (
                    <table className="w-full text-sm" data-testid="detail-campaign-table">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600 text-left">
                          <th className="px-4 py-2.5 font-medium">{tt('Кампания')}</th>
                          <th className="px-4 py-2.5 font-medium text-right">{tt('Лидов')}</th>
                          <th className="px-4 py-2.5 font-medium text-right">FTD</th>
                          <th className="px-4 py-2.5 font-medium text-right">{tt('Конверсия')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.campaignBreakdown.map((c, i) => (
                          <tr key={c.campaign} className={`border-t ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                            <td className="px-4 py-2.5 font-medium text-gray-800">{c.campaign}</td>
                            <td className="px-4 py-2.5 text-right text-blue-600">{c.leads}</td>
                            <td className="px-4 py-2.5 text-right text-green-600">{c.ftd}</td>
                            <td className="px-4 py-2.5 text-right">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                c.conversion >= 5 ? 'bg-green-100 text-green-700' : c.conversion > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                              }`}>{c.conversion}%</span>
                            </td>
                          </tr>
                        ))}
                        {data.campaignBreakdown.length === 0 && (
                          <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">{tt('Нет данных по кампаниям')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-sm" data-testid="detail-campaign-table">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600 text-left">
                          <th className="px-4 py-2.5 font-medium">{tt('Кампания')}</th>
                          <th className="px-4 py-2.5 font-medium text-right">{tt('Депозитов')}</th>
                          <th className="px-4 py-2.5 font-medium text-right">{tt('Тотал')}</th>
                          <th className="px-4 py-2.5 font-medium text-right">{tt('Сумма за месяц')}</th>
                          <th className="px-4 py-2.5 font-medium text-right">{tt('Релоады')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.campaignBreakdown.map((c, i) => (
                          <tr key={c.campaign} className={`border-t ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                            <td className="px-4 py-2.5 font-medium text-gray-800">{c.campaign}</td>
                            <td className="px-4 py-2.5 text-right text-gray-700">{c.totalDeposits}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-gray-700">{formatCurrency(c.totalAmount, true)}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-emerald-600">{formatCurrency(c.monthAmount, true)}</td>
                            <td className="px-4 py-2.5 text-right text-blue-600">{c.reloads}</td>
                          </tr>
                        ))}
                        {data.campaignBreakdown.length === 0 && (
                          <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">{tt('Нет данных по кампаниям')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {/* Client Deposits List */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-400" />
                {tt('Депозиты по клиентам')}
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{data.clientDeposits.length}</span>
              </h3>
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="detail-deposits-table">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-left">
                        <th className="px-4 py-2.5 font-medium">{tt('Клиент')}</th>
                        <th className="px-4 py-2.5 font-medium">{tt('Кампания')}</th>
                        <th className="px-4 py-2.5 font-medium text-right">{tt('Сумма')}</th>
                        <th className="px-4 py-2.5 font-medium">{tt('Тип')}</th>
                        {!isSales && <th className="px-4 py-2.5 font-medium">{tt('Статус')}</th>}
                        <th className="px-4 py-2.5 font-medium">{tt('Дата')}</th>
                        <th className="px-4 py-2.5 font-medium">{tt('Заметка')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.clientDeposits.map((d, i) => (
                        <tr key={i} className={`border-t ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                          <td className="px-4 py-2.5 font-medium text-gray-800">{d.clientName}</td>
                          <td className="px-4 py-2.5 text-gray-600 text-xs">{d.campaign}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-purple-600">{formatCurrency(d.amount, true)}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              d.depositType === 'ftd' ? 'bg-green-100 text-green-700' :
                              d.depositType === 'reload' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{d.depositType === 'ftd' ? 'FTD' : d.depositType === 'reload' ? 'Релоад' : d.depositType}</span>
                          </td>
                          {!isSales && (
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                d.isLead ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-600'
                              }`}>{d.isLead ? tt('Лид месяца') : tt('Старый клиент')}</span>
                            </td>
                          )}
                          <td className="px-4 py-2.5 text-gray-500 text-xs">{formatDate(d.date)}</td>
                          <td className="px-4 py-2.5 text-gray-400 text-xs max-w-[150px] truncate">{d.notes || '—'}</td>
                        </tr>
                      ))}
                      {data.clientDeposits.length === 0 && (
                        <tr><td colSpan={isSales ? 6 : 7} className="px-4 py-6 text-center text-gray-400">{tt('Нет депозитов')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MINI STAT ====================
const MiniStat = ({ label, value, color, formula }) => (
  <div className="bg-gray-50 rounded-lg p-3 relative">
    <div className="flex items-start justify-between mb-1">
      <div className="text-xs text-gray-500">{label}</div>
      {formula && (
        <button
          type="button"
          className="w-4 h-4 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 flex items-center justify-center transition-colors flex-shrink-0"
          title={formula}
          aria-label={formula}
          data-testid="ministat-help"
        >
          <HelpCircle className="w-3 h-3" />
        </button>
      )}
    </div>
    <div className={`text-lg font-bold ${color}`}>{value}</div>
  </div>
);

// ==================== STAT CARD ====================
const StatCard = ({ icon, label, value, bg, color, sub, formula }) => (
  <div className={`${bg} rounded-xl p-4 relative`}>
    {formula && (
      <button
        type="button"
        className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/80 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors shadow-sm"
        title={formula}
        aria-label={formula}
        data-testid="statcard-help"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
    )}
    <div className="flex items-center gap-2 mb-2 pr-6">
      {icon}
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

export default StatsPage;
